<template>
  <div class="knowledge-page">
    <div class="page-header d-flex justify-space-between align-center mb-5">
      <span class="text-h5">Knowledge review queue</span>
      <v-btn
        v-if="selectedIds.length > 1"
        color="primary"
        variant="flat"
        class="text-none"
        :loading="bulkLoading"
        @click="bulkApprove"
      >
        Approve selected ({{ selectedIds.length }})
      </v-btn>
    </div>

    <div v-if="loading" class="d-flex justify-center py-10">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="drafts.length === 0" class="empty-state">
      <v-icon size="42" color="grey">mdi-book-open-outline</v-icon>
      <div class="text-body-2 text-medium-emphasis mt-2">
        No drafts waiting for review.
      </div>
    </div>

    <div v-else class="draft-list">
      <div
        v-for="draft in drafts"
        :key="draft.ticketId"
        class="draft-card"
        :class="{ 'is-expanded': isExpanded(draft.ticketId) }"
      >
        <div class="draft-header" @click="toggleExpand(draft.ticketId)">
          <v-checkbox
            :model-value="selectedIds.includes(draft.ticketId)"
            hide-details
            density="compact"
            class="draft-checkbox"
            @click.stop
            @update:model-value="toggleSelect(draft.ticketId)"
          />

          <div class="draft-heading">
            <span class="draft-subject">{{ draft.subject }}</span>
            <span
              v-if="draft.confidence"
              class="confidence-chip"
              :style="confidenceStyle(draft.confidence)"
            >
              {{ draft.confidence }} confidence
            </span>
          </div>

          <div class="draft-meta">
            from case {{ draft.caseId
            }}<template v-if="draft.resolvedLabel"> · {{ draft.resolvedLabel }}</template>
          </div>

          <v-btn
            icon
            variant="text"
            size="small"
            class="expand-btn"
            :aria-expanded="isExpanded(draft.ticketId)"
            aria-label="Expand draft"
            @click.stop="toggleExpand(draft.ticketId)"
          >
            <v-icon
              class="expand-icon"
              :class="{ 'expand-icon-rotated': isExpanded(draft.ticketId) }"
            >
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </div>

        <v-expand-transition>
          <div v-show="isExpanded(draft.ticketId)" class="draft-body">
            <div class="field-block">
              <label class="field-label">Draft title</label>
              <v-text-field
                v-model="draft.title"
                variant="outlined"
                density="comfortable"
                hide-details
              />
            </div>

            <div class="field-block">
              <label class="field-label">Draft content</label>
              <div class="rich-text-editor" @click.stop>
                <v-card variant="outlined" class="editor-container">
                  <div class="editor-toolbar">
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.bold }"
                      :color="activeFormats.bold ? 'primary' : undefined"
                      title="Bold"
                      @mousedown.prevent
                      @click="formatText('bold', draft)"
                    >
                      <v-icon>mdi-format-bold</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.italic }"
                      :color="activeFormats.italic ? 'primary' : undefined"
                      title="Italic"
                      @mousedown.prevent
                      @click="formatText('italic', draft)"
                    >
                      <v-icon>mdi-format-italic</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.underline }"
                      :color="activeFormats.underline ? 'primary' : undefined"
                      title="Underline"
                      @mousedown.prevent
                      @click="formatText('underline', draft)"
                    >
                      <v-icon>mdi-format-underline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :class="{ active: activeFormats.unorderedList }"
                      :color="activeFormats.unorderedList ? 'primary' : undefined"
                      title="Bullet points"
                      @mousedown.prevent
                      @click="formatText('insertUnorderedList', draft)"
                    >
                      <v-icon>mdi-format-list-bulleted</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      tabindex="-1"
                      :loading="draft._uploading"
                      :disabled="draft._uploading"
                      title="Insert image"
                      @mousedown.prevent
                      @click="openImagePicker(draft)"
                    >
                      <v-icon>mdi-image-outline</v-icon>
                    </v-btn>
                  </div>

                  <v-divider />

                  <div
                    :ref="(el) => setEditorRef(draft.ticketId, el)"
                    class="editor-content"
                    contenteditable="true"
                    @input="handleEditorInput(draft)"
                    @keyup="updateToolbarState"
                    @mouseup="updateToolbarState"
                    @focus="updateToolbarState"
                    @click="updateToolbarState"
                    @paste="(event) => handleEditorPaste(event, draft)"
                    @dragover.prevent
                    @drop.prevent="(event) => handleEditorDrop(event, draft)"
                  ></div>
                </v-card>
              </div>
            </div>

            <div
              v-if="draft.attachments && draft.attachments.length"
              class="field-block"
            >
              <label class="field-label">
                Attachments
                <span class="attachment-hint">Drag to reorder</span>
              </label>
              <draggable
                v-model="draft.attachments"
                class="draft-attachments"
                :item-key="attachmentItemKey"
                :animation="200"
                ghost-class="attachment-ghost"
                chosen-class="attachment-chosen"
                drag-class="attachment-drag"
                filter=".attachment-remove"
                :prevent-on-filter="true"
              >
                <template #item="{ element: attachment, index: attachmentIndex }">
                  <div class="attachment-item" title="Drag to reorder">
                    <button
                      v-if="isImageAttachment(attachment)"
                      type="button"
                      class="attachment-link attachment-button"
                      @click="openImagePreview(attachment, draft.attachments)"
                    >
                      <img
                        :src="attachment.url"
                        :alt="
                          attachment.name || `Attachment ${attachmentIndex + 1}`
                        "
                        class="attachment-image"
                        loading="lazy"
                        draggable="false"
                      />
                    </button>
                    <a
                      v-else-if="attachment.url"
                      :href="attachment.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="attachment-link attachment-file"
                    >
                      <v-chip
                        size="small"
                        variant="outlined"
                        prepend-icon="mdi-paperclip"
                      >
                        {{
                          attachment.name || `Attachment ${attachmentIndex + 1}`
                        }}
                      </v-chip>
                    </a>

                    <v-btn
                      class="attachment-remove"
                      icon
                      size="x-small"
                      color="error"
                      variant="flat"
                      title="Remove attachment"
                      @click.stop="removeAttachment(draft, attachmentIndex)"
                    >
                      <v-icon size="16">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </template>
              </draggable>
            </div>

            <div class="draft-actions">
              <v-btn
                variant="outlined"
                color="grey"
                class="text-none"
                :loading="draft._rejecting"
                @click="rejectDraft(draft)"
              >
                Reject
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                class="text-none"
                :loading="draft._approving"
                @click="approveDraft(draft)"
              >
                Approve &amp; add to knowledge base
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top">
      {{ snackbar.message }}
    </v-snackbar>

    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      class="d-none"
      @change="onImageSelected"
    />

    <v-dialog v-model="imagePreview.show" max-width="960">
      <v-card class="image-preview-card">
        <v-card-title class="d-flex justify-space-between align-center pa-3">
          <span class="text-body-2 font-weight-medium">Attachment preview</span>
          <div class="d-flex align-center" style="gap: 4px">
            <template v-if="imagePreview.urls.length > 1">
              <v-btn
                icon
                variant="text"
                size="small"
                :disabled="imagePreview.index <= 0"
                @click="showPrevImage"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="text-caption text-medium-emphasis px-1">
                {{ imagePreview.index + 1 }} / {{ imagePreview.urls.length }}
              </span>
              <v-btn
                icon
                variant="text"
                size="small"
                :disabled="imagePreview.index >= imagePreview.urls.length - 1"
                @click="showNextImage"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </template>
            <v-btn icon variant="text" size="small" @click="closeImagePreview">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="image-preview-body pa-4">
          <img
            v-if="imagePreview.url"
            :src="imagePreview.url"
            alt="Attachment preview"
            class="preview-image"
          />
        </v-card-text>

      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import draggable from "vuedraggable";
import { list_knowledge_reviews } from "@/graphql/queries";
import {
  approve_knowledge,
  bulk_approve_knowledge,
  reject_knowledge,
  get_knowledge_upload_url,
} from "@/graphql/mutations";

const client = generateClient();

export default {
  name: "RCSSupportKnowledge",
  components: {
    draggable,
  },
  data() {
    return {
      loading: true,
      bulkLoading: false,
      selectedIds: [],
      expandedIds: [],
      drafts: [],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      imagePreview: {
        show: false,
        url: "",
        urls: [],
        index: 0,
      },
      editorRefs: {},
      uploadTargetDraftId: null,
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
        unorderedList: false,
      },
    };
  },
  async mounted() {
    await this.loadDrafts();
  },
  methods: {
    setEditorRef(ticketId, el) {
      if (el) {
        this.editorRefs[ticketId] = el;
      } else {
        delete this.editorRefs[ticketId];
      }
    },
    getEditorEl(ticketId) {
      return this.editorRefs[ticketId] || null;
    },
    toEditorHtml(content) {
      const value = content == null ? "" : String(content);
      if (!value) return "";
      const withoutImages = this.stripImagesFromHtml(value);
      if (/<[a-z][\s\S]*>/i.test(withoutImages)) return withoutImages;
      return withoutImages
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    },
    stripImagesFromHtml(html) {
      const value = String(html || "");
      if (!value) return "";
      return value
        .replace(/<img\b[^>]*>/gi, "")
        .replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, "");
    },
    async syncEditorContent(ticketId) {
      await this.$nextTick();
      const draft = this.drafts.find((item) => item.ticketId === ticketId);
      const editor = this.getEditorEl(ticketId);
      if (!draft || !editor) return;
      const cleaned = this.toEditorHtml(draft.content);
      editor.innerHTML = cleaned;
      draft.content = cleaned;
    },
    formatText(command, draft) {
      const editor = this.getEditorEl(draft.ticketId);
      if (editor) {
        editor.focus();
      }

      document.execCommand(command, false, null);

      this.$nextTick(() => {
        this.updateToolbarState();
        if (editor) {
          draft.content = this.stripImagesFromHtml(editor.innerHTML);
          if (editor.innerHTML !== draft.content) {
            editor.innerHTML = draft.content;
          }
        }
      });
    },
    handleEditorInput(draft) {
      const editor = this.getEditorEl(draft.ticketId);
      if (!editor) return;
      const cleaned = this.stripImagesFromHtml(editor.innerHTML);
      if (editor.innerHTML !== cleaned) {
        editor.innerHTML = cleaned;
      }
      draft.content = cleaned;
    },
    openImagePicker(draft) {
      this.uploadTargetDraftId = draft.ticketId;
      if (this.$refs.imageInput) {
        this.$refs.imageInput.value = "";
        this.$refs.imageInput.click();
      }
    },
    async onImageSelected(event) {
      const file = event?.target?.files?.[0];
      const draft = this.drafts.find(
        (item) => item.ticketId === this.uploadTargetDraftId
      );
      event.target.value = "";
      this.uploadTargetDraftId = null;
      if (!file || !draft) return;
      await this.uploadImageAttachment(draft, file);
    },
    async handleEditorPaste(event, draft) {
      const items = Array.from(event.clipboardData?.items || []);
      const imageItem = items.find((item) => item.type?.startsWith("image/"));
      if (!imageItem) return;
      event.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        await this.uploadImageAttachment(draft, file);
      }
    },
    async handleEditorDrop(event, draft) {
      const file = Array.from(event.dataTransfer?.files || []).find((item) =>
        item.type?.startsWith("image/")
      );
      if (!file) return;
      await this.uploadImageAttachment(draft, file);
    },
    parseUploadPayload(rawPayload) {
      const responseData =
        typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
      const data =
        responseData?.data && typeof responseData.data === "object"
          ? responseData.data
          : responseData || {};

      let fields = data.fields || data.formFields || null;
      if (typeof fields === "string") {
        try {
          fields = JSON.parse(fields);
        } catch (_) {
          fields = null;
        }
      }

      const uploadUrl =
        data.uploadUrl ||
        data.upload_url ||
        data.presignedUrl ||
        data.presigned_url ||
        (fields ? data.url : null) ||
        data.url ||
        "";

      const key =
        data.key ||
        data.fileKey ||
        data.file_key ||
        data.s3Key ||
        data.s3_key ||
        data.path ||
        fields?.key ||
        "";

      const viewUrl =
        data.downloadUrl ||
        data.download_url ||
        data.fileUrl ||
        data.file_url ||
        data.previewUrl ||
        data.preview_url ||
        data.readUrl ||
        data.read_url ||
        (!fields ? data.url : "") ||
        "";

      return {
        status: responseData?.status,
        success: responseData?.success,
        message: responseData?.status_message || responseData?.message || "",
        uploadUrl,
        key,
        viewUrl,
        contentType: data.contentType || data.content_type || "",
        fields,
      };
    },
    async putFileToUrl(uploadUrl, file, contentType = "") {
      const headers = {
        "Content-Type":
          contentType || file.type || "application/octet-stream",
      };
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers,
        body: file,
      });
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
    },
    async postFileToUrl(uploadUrl, fields, file) {
      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file);
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
    },
    async uploadImageAttachment(draft, file) {
      if (!file?.type?.startsWith("image/")) {
        this.showSnack("Please select an image file", "error");
        return;
      }

      draft._uploading = true;
      try {
        const response = await client.graphql({
          query: get_knowledge_upload_url,
          variables: {
            input: {
              file_name: file.name,
            },
          },
        });

        const parsed = this.parseUploadPayload(
          response.data.get_knowledge_upload_url
        );

        if (
          !(parsed.status === "SUCCESS" || parsed.success === true) &&
          !parsed.uploadUrl
        ) {
          throw new Error(parsed.message || "Failed to get upload URL");
        }
        if (!parsed.uploadUrl) {
          throw new Error("Upload URL missing from response");
        }

        if (parsed.fields) {
          await this.postFileToUrl(parsed.uploadUrl, parsed.fields, file);
        } else {
          await this.putFileToUrl(
            parsed.uploadUrl,
            file,
            parsed.contentType || file.type
          );
        }

        const localPreviewUrl = URL.createObjectURL(file);
        const displayUrl = parsed.viewUrl || localPreviewUrl;
        const attachmentKey =
          this.extractS3Key(parsed.key) || parsed.key || file.name;

        const attachment = {
          _uid: `att-${attachmentKey}-${Date.now()}`,
          key: attachmentKey,
          url: displayUrl,
          name: file.name,
        };

        if (!Array.isArray(draft.attachments)) {
          draft.attachments = [];
        }
        draft.attachments.push(attachment);

        // Keep draft content text-only; images belong in Attachments.
        const editor = this.getEditorEl(draft.ticketId);
        if (editor) {
          const cleaned = this.stripImagesFromHtml(editor.innerHTML);
          editor.innerHTML = cleaned;
          draft.content = cleaned;
        }

        this.showSnack("Image uploaded");
      } catch (error) {
        console.error("Image upload failed:", error);
        this.showSnack(error.message || "Failed to upload image", "error");
      } finally {
        draft._uploading = false;
      }
    },
    getAttachmentKeys(draft) {
      return (draft?.attachments || [])
        .map((item) => this.extractS3Key(item.key || item.url || ""))
        .filter(Boolean);
    },
    updateToolbarState() {
      this.$nextTick(() => {
        const activeEditor = Object.values(this.editorRefs).find(
          (el) => el === document.activeElement
        );
        if (!activeEditor) return;

        try {
          this.activeFormats.bold = document.queryCommandState("bold");
          this.activeFormats.italic = document.queryCommandState("italic");
          this.activeFormats.underline = document.queryCommandState("underline");
          this.activeFormats.unorderedList =
            document.queryCommandState("insertUnorderedList");
        } catch (_) {
          this.activeFormats.bold = false;
          this.activeFormats.italic = false;
          this.activeFormats.underline = false;
          this.activeFormats.unorderedList = false;
        }
      });
    },
    toggleSelect(ticketId) {
      const idx = this.selectedIds.indexOf(ticketId);
      if (idx === -1) {
        this.selectedIds.push(ticketId);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },
    isExpanded(ticketId) {
      return this.expandedIds.includes(ticketId);
    },
    toggleExpand(ticketId) {
      const idx = this.expandedIds.indexOf(ticketId);
      if (idx === -1) {
        this.expandedIds.push(ticketId);
        this.syncEditorContent(ticketId);
      } else {
        this.expandedIds.splice(idx, 1);
      }
    },
    formatConfidence(value) {
      if (!value) return "";
      const normalized = String(value).trim().toLowerCase();
      if (!normalized) return "";
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    },
    confidenceStyle(level) {
      const map = { high: "#43A047", medium: "#FB8C00", low: "#78909C" };
      const key = String(level || "").trim().toLowerCase();
      const hex = map[key] || "#FB8C00";
      return { color: hex, background: `${hex}1A` };
    },
    attachmentItemKey(item) {
      return item?._uid || item?.key || item?.url || item?.name;
    },
    attachmentFileName(pathOrUrl) {
      const path = String(pathOrUrl || "").split("?")[0];
      const parts = path.split("/");
      return parts[parts.length - 1] || "Attachment";
    },
    extractS3Key(pathOrUrl) {
      const value = String(pathOrUrl || "").trim();
      if (!value) return "";
      if (value.startsWith("http")) {
        try {
          const parsed = new URL(value);
          return decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
        } catch (_) {
          return value.split("?")[0].replace(/^\/+/, "");
        }
      }
      return value.split("?")[0].replace(/^\/+/, "");
    },
    normalizeAttachmentItem(item) {
      if (!item) return null;
      if (typeof item === "string") {
        const value = item.trim();
        if (!value) return null;
        const isHttp = value.startsWith("http");
        const key = this.extractS3Key(value);
        return {
          _uid: `att-${key || value}`,
          key: key || value,
          url: isHttp ? value : "",
          name: this.attachmentFileName(key || value),
        };
      }
      if (typeof item === "object") {
        const url = item.url || item.signedUrl || item.signed_url || "";
        const key =
          this.extractS3Key(item.key || item.path || "") ||
          this.extractS3Key(url);
        if (!key && !url) return null;
        return {
          _uid: item._uid || `att-${key || url}`,
          key: key || url,
          url,
          name: this.attachmentFileName(key || url),
        };
      }
      return null;
    },
    normalizeAttachments(value) {
      let list = [];
      if (Array.isArray(value)) {
        list = value;
      } else if (typeof value === "string" && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          list = Array.isArray(parsed) ? parsed : [value];
        } catch (_) {
          list = [value];
        }
      }
      return list.map((item) => this.normalizeAttachmentItem(item)).filter(Boolean);
    },
    removeAttachment(draft, attachmentIndex) {
      if (!draft?.attachments?.length) return;
      const removed = draft.attachments[attachmentIndex];
      if (!removed) return;

      draft.attachments.splice(attachmentIndex, 1);

      const editor = this.getEditorEl(draft.ticketId);
      if (editor && removed.url) {
        editor.querySelectorAll("img").forEach((img) => {
          const src = img.getAttribute("src") || "";
          if (
            src === removed.url ||
            this.extractS3Key(src) === this.extractS3Key(removed.key || removed.url)
          ) {
            img.remove();
          }
        });
        draft.content = editor.innerHTML;
      }
    },
    isImageAttachment(attachment) {
      const path = String(attachment?.key || attachment?.url || "").split("?")[0];
      return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(path);
    },
    openImagePreview(attachment, attachments = []) {
      const imageUrls = (attachments || [])
        .filter((item) => this.isImageAttachment(item) && item.url)
        .map((item) => item.url);
      const currentUrl = attachment?.url || "";
      const urls = imageUrls.length ? imageUrls : currentUrl ? [currentUrl] : [];
      if (!urls.length) return;
      const index = Math.max(0, urls.indexOf(currentUrl));
      this.imagePreview = {
        show: true,
        url: urls[index] || urls[0],
        urls,
        index: urls[index] ? index : 0,
      };
    },
    closeImagePreview() {
      this.imagePreview.show = false;
    },
    showPrevImage() {
      if (this.imagePreview.index <= 0) return;
      this.imagePreview.index -= 1;
      this.imagePreview.url = this.imagePreview.urls[this.imagePreview.index];
    },
    showNextImage() {
      if (this.imagePreview.index >= this.imagePreview.urls.length - 1) return;
      this.imagePreview.index += 1;
      this.imagePreview.url = this.imagePreview.urls[this.imagePreview.index];
    },
    showSnack(message, color = "success") {
      this.snackbar = { show: true, message, color };
    },
    async loadDrafts() {
      this.loading = true;
      try {
        const response = await client.graphql({ query: list_knowledge_reviews });
        const raw = response.data.list_knowledge_reviews;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status === "SUCCESS") {
          const items = Array.isArray(result.data) ? result.data : result.data?.items || [];
          this.drafts = items.map((item) => ({
            ticketId: item.ticket_id || item.ticketId || item.id,
            caseId: item.ticket_id || item.ticketId || item.case_id || item.caseId || "",
            subject: item.subject || item.title || "Untitled",
            confidence: this.formatConfidence(
              item.kb_draft_confidence || item.confidence || ""
            ),
            resolvedLabel: item.resolved_label || item.resolvedLabel || "",
            title: item.kb_draft_title || item.editedTitle || item.title || "",
            content: item.kb_draft_content || item.editedContent || item.content || "",
            attachments: this.normalizeAttachments(
              item.kb_draft_attachments?.length
                ? item.kb_draft_attachments
                : item.attachments || []
            ),
            _approving: false,
            _rejecting: false,
            _uploading: false,
          }));
          if (this.drafts.length === 1) {
            this.expandedIds = [this.drafts[0].ticketId];
          }
        } else {
          this.drafts = [];
        }
      } catch (error) {
        console.error("Failed to load knowledge reviews:", error);
        this.drafts = [];
        this.showSnack("Failed to load reviews", "error");
      } finally {
        this.loading = false;
      }

      await this.$nextTick();
      for (const ticketId of this.expandedIds) {
        await this.syncEditorContent(ticketId);
      }
    },
    async approveDraft(draft) {
      draft._approving = true;
      try {
        const response = await client.graphql({
          query: approve_knowledge,
          variables: {
            input: {
              ticket_id: draft.ticketId,
              edited_title: draft.title,
              edited_content: draft.content,
              kb_attachments: this.getAttachmentKeys(draft),
            },
          },
        });
        const raw = response.data.approve_knowledge;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status === "SUCCESS") {
          this.drafts = this.drafts.filter((d) => d.ticketId !== draft.ticketId);
          this.selectedIds = this.selectedIds.filter((id) => id !== draft.ticketId);
          this.expandedIds = this.expandedIds.filter((id) => id !== draft.ticketId);
          this.showSnack(`"${draft.title}" approved and added to knowledge base.`);
        } else {
          throw new Error(result.status_message || "Approve failed");
        }
      } catch (error) {
        console.error("Approve failed:", error);
        this.showSnack(error.message || "Failed to approve", "error");
      } finally {
        draft._approving = false;
      }
    },
    async rejectDraft(draft) {
      draft._rejecting = true;
      try {
        const response = await client.graphql({
          query: reject_knowledge,
          variables: { input: { ticket_id: draft.ticketId } },
        });
        const raw = response.data.reject_knowledge;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status === "SUCCESS") {
          this.drafts = this.drafts.filter((d) => d.ticketId !== draft.ticketId);
          this.selectedIds = this.selectedIds.filter((id) => id !== draft.ticketId);
          this.expandedIds = this.expandedIds.filter((id) => id !== draft.ticketId);
          this.showSnack(`Draft from case ${draft.caseId} rejected.`, "warning");
        } else {
          throw new Error(result.status_message || "Reject failed");
        }
      } catch (error) {
        console.error("Reject failed:", error);
        this.showSnack(error.message || "Failed to reject", "error");
      } finally {
        draft._rejecting = false;
      }
    },
    async bulkApprove() {
      this.bulkLoading = true;
      try {
        const response = await client.graphql({
          query: bulk_approve_knowledge,
          variables: { input: { ticket_ids: this.selectedIds } },
        });
        const raw = response.data.bulk_approve_knowledge;
        const result = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (result.status === "SUCCESS") {
          const count = this.selectedIds.length;
          this.drafts = this.drafts.filter(
            (d) => !this.selectedIds.includes(d.ticketId)
          );
          this.expandedIds = this.expandedIds.filter(
            (id) => !this.selectedIds.includes(id)
          );
          this.selectedIds = [];
          this.showSnack(`${count} drafts approved and added to knowledge base.`);
        } else {
          throw new Error(result.status_message || "Bulk approve failed");
        }
      } catch (error) {
        console.error("Bulk approve failed:", error);
        this.showSnack(error.message || "Failed to bulk approve", "error");
      } finally {
        this.bulkLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.knowledge-page {
  background: #fff;
  border-radius: 8px;
  padding: 8px 8px 24px;
}

.draft-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.draft-card {
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 18px 20px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.draft-card.is-expanded {
  border-color: #cfd8e3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.draft-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  min-width: 0;
}

.draft-checkbox {
  flex: 0 0 auto;
  width: 36px;
  margin: 0;
  padding: 0;
}

.draft-checkbox :deep(.v-selection-control) {
  min-height: 28px;
  width: 36px;
}

.draft-checkbox :deep(.v-checkbox .v-selection-control__wrapper),
.draft-checkbox :deep(.v-selection-control__wrapper) {
  margin-inline-end: 0;
}

.draft-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}

.draft-subject {
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  line-height: 1.3;
}

.confidence-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.draft-meta {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.draft-body {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #eef2f6;
}

.expand-btn {
  flex-shrink: 0;
}

.expand-icon {
  transition: transform 0.2s ease;
}

.expand-icon-rotated {
  transform: rotate(180deg);
}

.field-block {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #212121;
}

.attachment-hint {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
}

.rich-text-editor {
  width: 100%;
}

.editor-container {
  width: 100%;
  overflow: hidden;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 6px;
}

.editor-toolbar .v-btn.active,
.editor-toolbar .v-btn[color="primary"] {
  background-color: rgba(var(--v-theme-primary), 0.2) !important;
  color: rgb(var(--v-theme-primary)) !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.3) !important;
}

.editor-toolbar .v-btn.active .v-icon,
.editor-toolbar .v-btn[color="primary"] .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.editor-content {
  min-height: 180px;
  max-height: 360px;
  overflow-y: auto;
  padding: 12px;
  outline: none;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
}

.editor-content:empty::before {
  content: "Enter draft content...";
  color: rgba(0, 0, 0, 0.38);
}

.editor-content strong,
.editor-content b {
  font-weight: bold;
}

.editor-content em,
.editor-content i {
  font-style: italic;
}

.editor-content u {
  text-decoration: underline;
}

.editor-content ul {
  margin: 0 0 0 1.25rem;
  padding: 0;
}

.editor-content li {
  margin: 0.15rem 0;
}

.editor-content img {
  display: none !important;
}

.draft-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-item {
  position: relative;
  display: inline-flex;
  cursor: grab;
  user-select: none;
}

.attachment-item:active {
  cursor: grabbing;
}

.attachment-ghost {
  opacity: 0.35;
}

.attachment-chosen {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  border-radius: 8px;
}

.attachment-drag {
  opacity: 0.9;
  cursor: grabbing;
}

.attachment-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  min-width: 22px !important;
  width: 22px;
  height: 22px;
}

.attachment-link {
  display: block;
  line-height: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: box-shadow 0.15s ease;
}

.attachment-link:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.attachment-button {
  padding: 0;
  background: none;
  cursor: pointer;
}

.attachment-file {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  text-decoration: none;
}

.attachment-image {
  display: block;
  max-width: 220px;
  max-height: 160px;
  width: auto;
  height: auto;
  object-fit: cover;
}

.image-preview-card {
  overflow: hidden;
}

.image-preview-body {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  max-height: 75vh;
  overflow: auto;
  background: #f5f5f5;
}

.preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  object-fit: contain;
}

.draft-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.empty-state {
  text-align: center;
  padding: 64px 16px;
  border: 1px dashed #e8edf2;
  border-radius: 12px;
}

@media (max-width: 700px) {
  .draft-header {
    flex-wrap: wrap;
  }

  .draft-meta {
    order: 3;
    margin-left: 38px;
    white-space: normal;
  }

  .expand-btn {
    margin-left: auto;
  }

  .draft-actions {
    width: 100%;
  }

  .draft-actions .v-btn {
    flex: 1;
  }
}
</style>
